import { gql } from '@apollo/client';

export const fragments = {
  all: gql`
    fragment ScreenData on Screen {
      id
      code
      connection {
        type {
          type
          room {
            id
            name
          }
        }
        active
        clientVersion
        lastDisconnectedAt
        lastConnectedAt
        lastCodeRequestedAt
        ipAddress
        connectionCount
        totalUptime
      }
      configuration {
        description
        showProcedure
        patientNameFormat
        physicianNameFormat
        dischargeSound
        orReadySound
        waitingReadySound
        pacuReadySound
        prepReadySound
        readyForSurgeonSound
        readyToPickupSound
        holdProcedureSound
        blockNerveSound
        procedureEndSound
        closingSound
        helpSound
        sendNotifications
        preOpFocused
        pacuFocused
        expandedOrTileView
        isTimeline
        limit
        minimumRating
        showBedNumber
        showStaffListOnSchedule
        useCssGridLayout
        showPostOp
        chartingPinTimeout
        questionnaireSessionTimeout
        showRideHome
        showCaretakerWr
        showPreOpWr
        showIntakeWr
        showOrRooms
        lightMode
        compactInitials
        wrapProcedureName
        highlightNotes
        hideNotes
        highlightAllergies
        anesthesiologistSchedule
        messagingScreen
        showDatePickerOnSchedule
        showPatientTypes
        showPhysicians
        showScheduledProcedureDuration
        screenVersion
        orthopedic
        showLatePreOpIndicator
        timelineViewRoomTime
        dischargeModalV2
        enableFlipRoom
        enableHelp
        enabledReturnPatient
        showStaffIndicators
        showPatientNotesOnInternalScreen
        showDelayReasons
        showProcedureDelayReasonModal
        showScheduleColumn
        scheduleColumnView
        scheduleColumnWidth
        scheduleColumnMaxColumns
        scheduleColumnGroupByTime
        scheduleColumnCarouselIntervalSeconds
        patientCardShowAgeAndSex
        patientCardShowProcedureTimes
        patientCardSortOrder
      }
    }
  `,
};

export const list = gql`
  subscription {
    screens {
      ...ScreenData
    }
  }
  ${fragments.all}
`;

export const get = gql`
  query screen($screenCode: String!) {
    screen(screenCode: $screenCode) {
      ...ScreenData
    }
  }
  ${fragments.all}
`;

export const screenConfiguration = gql`
  subscription {
    screenConfiguration {
      description
      showProcedure
      patientNameFormat
      physicianNameFormat
      dischargeSound
      orReadySound
      waitingReadySound
      pacuReadySound
      prepReadySound
      readyForSurgeonSound
      readyToPickupSound
      holdProcedureSound
      blockNerveSound
      procedureEndSound
      closingSound
      helpSound
      sendNotifications
      preOpFocused
      pacuFocused
      expandedOrTileView
      isTimeline
      showBedNumber
      showStaffListOnSchedule
      useCssGridLayout
      showPostOp
      chartingPinTimeout
      questionnaireSessionTimeout
      showRideHome
      showCaretakerWr
      showPreOpWr
      showIntakeWr
      showOrRooms
      compactInitials
      wrapProcedureName
      highlightNotes
      hideNotes
      highlightAllergies
      lightMode
      anesthesiologistSchedule
      messagingScreen
      showDatePickerOnSchedule
      showPatientTypes
      showPhysicians
      showScheduledProcedureDuration
      screenVersion
      orthopedic
      showLatePreOpIndicator
      timelineViewRoomTime
      dischargeModalV2
      enableFlipRoom
      enableHelp
      enabledReturnPatient
      showStaffIndicators
      showPatientNotesOnInternalScreen
      showDelayReasons
      showProcedureDelayReasonModal
      showScheduleColumn
      scheduleColumnView
      scheduleColumnWidth
      scheduleColumnMaxColumns
      scheduleColumnGroupByTime
      scheduleColumnCarouselIntervalSeconds
      patientCardShowAgeAndSex
      patientCardShowProcedureTimes
      patientCardSortOrder
    }
  }
`;

export const approveScreen = gql`
  mutation approveScreen($screenCode: String!, $screenType: IScreenType!) {
    approveScreen(screenCode: $screenCode, screenType: $screenType)
  }
`;

export const removeScreen = gql`
  mutation removeScreen($screenCode: String!) {
    removeScreen(screenCode: $screenCode)
  }
`;

export const updateScreen = gql`
  mutation updateScreen(
    $screenCode: String!
    $description: String
    $showProcedure: Boolean
    $patientNameFormat: PatientNameFormat
    $physicianNameFormat: PhysicianNameFormat
    $dischargeSound: Boolean
    $orReadySound: Boolean
    $waitingReadySound: Boolean
    $pacuReadySound: Boolean
    $prepReadySound: Boolean
    $readyForSurgeonSound: Boolean
    $readyToPickupSound: Boolean
    $holdProcedureSound: Boolean
    $blockNerveSound: Boolean
    $procedureEndSound: Boolean
    $closingSound: Boolean
    $helpSound: Boolean
    $sendNotifications: Boolean
    $preOpFocused: Boolean
    $pacuFocused: Boolean
    $expandedOrTileView: Boolean
    $isTimeline: Boolean
    $limit: Int
    $minimumRating: Int
    $showBedNumber: Boolean
    $showStaffListOnSchedule: Boolean
    $useCssGridLayout: Boolean
    $showPostOp: Boolean
    $chartingPinTimeout: Int
    $questionnaireSessionTimeout: Int
    $showRideHome: Boolean
    $showCaretakerWr: Boolean
    $showPreOpWr: Boolean
    $showIntakeWr: Boolean
    $showOrRooms: [Long!]
    $lightMode: Boolean
    $compactInitials: Boolean
    $wrapProcedureName: Boolean
    $highlightNotes: Boolean
    $hideNotes: Boolean
    $highlightAllergies: Boolean
    $anesthesiologistSchedule: Boolean
    $messagingScreen: Boolean
    $showDatePickerOnSchedule: Boolean
    $showPatientTypes: [String!]
    $showPhysicians: [Long!]
    $showScheduledProcedureDuration: Boolean
    $screenVersion: Int
    $orthopedic: Boolean
    $showLatePreOpIndicator: Boolean
    $timelineViewRoomTime: Boolean
    $dischargeModalV2: Boolean
    $enableFlipRoom: Boolean
    $enableHelp: Boolean
    $enabledReturnPatient: Boolean
    $showStaffIndicators: Boolean
    $showPatientNotesOnInternalScreen: Boolean
    $showDelayReasons: Boolean
    $showProcedureDelayReasonModal: Boolean
    $showScheduleColumn: Boolean
    $scheduleColumnView: ScheduleColumnView
    $scheduleColumnWidth: Int
    $scheduleColumnMaxColumns: Int
    $scheduleColumnGroupByTime: Boolean
    $scheduleColumnCarouselIntervalSeconds: Int
    $patientCardShowAgeAndSex: Boolean
    $patientCardShowProcedureTimes: Boolean
    $patientCardSortOrder: PatientCardSortOrder
  ) {
    updateScreen(
      screenCode: $screenCode
      description: $description
      showProcedure: $showProcedure
      patientNameFormat: $patientNameFormat
      physicianNameFormat: $physicianNameFormat
      dischargeSound: $dischargeSound
      orReadySound: $orReadySound
      waitingReadySound: $waitingReadySound
      pacuReadySound: $pacuReadySound
      prepReadySound: $prepReadySound
      readyForSurgeonSound: $readyForSurgeonSound
      readyToPickupSound: $readyToPickupSound
      holdProcedureSound: $holdProcedureSound
      blockNerveSound: $blockNerveSound
      procedureEndSound: $procedureEndSound
      closingSound: $closingSound
      helpSound: $helpSound
      sendNotifications: $sendNotifications
      preOpFocused: $preOpFocused
      pacuFocused: $pacuFocused
      expandedOrTileView: $expandedOrTileView
      isTimeline: $isTimeline
      limit: $limit
      minimumRating: $minimumRating
      showBedNumber: $showBedNumber
      showStaffListOnSchedule: $showStaffListOnSchedule
      useCssGridLayout: $useCssGridLayout
      showPostOp: $showPostOp
      chartingPinTimeout: $chartingPinTimeout
      questionnaireSessionTimeout: $questionnaireSessionTimeout
      showRideHome: $showRideHome
      showCaretakerWr: $showCaretakerWr
      showPreOpWr: $showPreOpWr
      showIntakeWr: $showIntakeWr
      showOrRooms: $showOrRooms
      lightMode: $lightMode
      compactInitials: $compactInitials
      wrapProcedureName: $wrapProcedureName
      highlightNotes: $highlightNotes
      hideNotes: $hideNotes
      highlightAllergies: $highlightAllergies
      anesthesiologistSchedule: $anesthesiologistSchedule
      messagingScreen: $messagingScreen
      showDatePickerOnSchedule: $showDatePickerOnSchedule
      showPatientTypes: $showPatientTypes
      showPhysicians: $showPhysicians
      showScheduledProcedureDuration: $showScheduledProcedureDuration
      screenVersion: $screenVersion
      orthopedic: $orthopedic
      showLatePreOpIndicator: $showLatePreOpIndicator
      timelineViewRoomTime: $timelineViewRoomTime
      dischargeModalV2: $dischargeModalV2
      enableFlipRoom: $enableFlipRoom
      enableHelp: $enableHelp
      enabledReturnPatient: $enabledReturnPatient
      showStaffIndicators: $showStaffIndicators
      showPatientNotesOnInternalScreen: $showPatientNotesOnInternalScreen
      showDelayReasons: $showDelayReasons
      showProcedureDelayReasonModal: $showProcedureDelayReasonModal
      showScheduleColumn: $showScheduleColumn
      scheduleColumnView: $scheduleColumnView
      scheduleColumnWidth: $scheduleColumnWidth
      scheduleColumnMaxColumns: $scheduleColumnMaxColumns
      scheduleColumnGroupByTime: $scheduleColumnGroupByTime
      scheduleColumnCarouselIntervalSeconds: $scheduleColumnCarouselIntervalSeconds
      patientCardShowAgeAndSex: $patientCardShowAgeAndSex
      patientCardShowProcedureTimes: $patientCardShowProcedureTimes
      patientCardSortOrder: $patientCardSortOrder
    )
  }
`;

export default {
  list,
};
